import React, { Fragment } from "react";

import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { Alert, AlertDescription } from "@/src/components/ui/alert";
import { Button } from "@/src/components/ui/button";
import { Card, CardContent } from "@/src/components/ui/card";
import { Skeleton } from "@/src/components/ui/skeleton";

import BulkExportActions from "./BulkExportActions";
import BulkObjectList from "./BulkObjectList";
import BulkPageHeader from "./BulkPageHeader";
import BulkPageLayout from "./BulkPageLayout";
import BulkStatusBanner from "./BulkStatusBanner";
import BulkStorefrontProjectField from "./BulkStorefrontProjectField";
import useBulkIngest from "../../hooks/useBulkIngest";

import type { JSX, ReactNode } from "react";

const CARD_CLASS_NAME = "flex flex-1 flex-col";

const CARD_CONTENT_CLASS_NAME = "flex flex-1 flex-col gap-4";

function BulkDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation("editor-bulk");

  const {
    ingest,
    isBusy,
    isError,
    isLoading,
    isSettingStorefrontProject,
    pendingStorefrontProjectId,
    refetch,
    requiresStorefrontProject,
    setStorefrontProject,
    startIngest,
    startIngestBlockedReason,
    status,
    storefrontProjectId,
    summary,
  } = useBulkIngest(id);

  const loaded =
    isLoading || isError || ingest === undefined || status === undefined
      ? null
      : { ingest, status };

  function renderContent(): ReactNode {
    if (isLoading) {
      return (
        <Fragment>
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-10 w-64 max-w-full" />
          <Skeleton className="min-h-64 w-full grow basis-64" />
        </Fragment>
      );
    }

    if (loaded === null) {
      return (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between gap-3">
            {t("loadError")}
            <Button onClick={refetch} type="button" variant="outline">
              {t("retry")}
            </Button>
          </AlertDescription>
        </Alert>
      );
    }

    const isFinished =
      loaded.status === "complete" || loaded.status === "failed";
    const showsStorefrontProjectField =
      loaded.status === "created" && requiresStorefrontProject;

    return (
      <Fragment>
        <BulkStatusBanner
          actions={isFinished ? <BulkExportActions bulkId={id} /> : undefined}
          onStartIngest={startIngest}
          startIngestBlockedReason={startIngestBlockedReason}
          status={loaded.status}
          summary={summary}
        >
          {showsStorefrontProjectField ? (
            <BulkStorefrontProjectField
              isDisabled={isBusy}
              isSaving={isSettingStorefrontProject}
              onChange={setStorefrontProject}
              value={pendingStorefrontProjectId ?? storefrontProjectId}
            />
          ) : null}
        </BulkStatusBanner>

        <BulkObjectList objectList={loaded.ingest.data} summary={summary} />
      </Fragment>
    );
  }

  const content = renderContent();
  const more = loaded?.ingest.more;
  const filename = more?.bulk_ingest_filename ?? "";

  return (
    <BulkPageLayout>
      <BulkPageHeader
        owner={more?.bulk_ingest_owner}
        reference={filename.length > 0 ? filename : more?.bulk_ingest_uuid}
        status={loaded?.status}
        title={t("detailTitle")}
      />

      <Card className={CARD_CLASS_NAME}>
        <CardContent className={CARD_CONTENT_CLASS_NAME}>{content}</CardContent>
      </Card>
    </BulkPageLayout>
  );
}

export default BulkDetail;
